/* eslint-disable linebreak-style */
import { Autocomplete, Box, Grid, TextField, Typography, CircularProgress } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useState } from 'react'
import { addDays } from 'date-fns'
import { RootStore } from '../../../redux/store'
import { bookingService } from '../../../services/booking/bookingService'
import { FindPersonEmployee } from '../../../services/booking/types'
import { showComponents } from '../utils'
import actionsService from '../../../services/actionsService'
import {
  setBookingFloorplanViewingDate,
  setDeskBookingFloorplanManuallySelected,
  setFoundDeskFeatureId,
  setFoundDeskName,
  clearFoundDesk,
} from '../../../redux/reducers/deskBookingReducer'
import { setDeskBookingSearchParams } from '../../../redux/reducers/deskBookingSearchReducer'
import { handleSearch } from '../bookingLogic'
import { BookingSearchType } from './enums'
import { setBookingWizardStepState } from '../../../redux/reducers/deskBookingWizardReducer'
import { BookingWizardSteps } from '../BlockBooking/enums'
import { BookingSearchParams } from '../types'
import { FindPersonDetails } from './FindPersonDetails'

// Helper function to format time without seconds (HH:MM:SS -> HH:MM)
const formatTimeWithoutSeconds = (time: string): string => {
  if (!time) return ''
  const parts = time.split(':')
  return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time
}

interface PersonOption {
  displayName: string
  employeeId: number
  deskName: string
  floorPlanName: string
}

interface FindPersonProps {
  mode?: 'search' | 'filter'
  onShowMeShared?: (bookingData: BookingSearchParams) => Promise<void>
}


const getBookingDetailsForPerson = async (
  person: PersonOption
): Promise<FindPersonEmployee[]> => {
  const response = await bookingService.findPerson(person.employeeId, person.displayName)
  
  // API now returns all bookings with complete details - use directly
  return response.employees || []
}
/* Shared helper to display the right-hand side floorplan panel and load/highlight features */
const displayFloorplanForBooking = async (
  dispatch: any,
  zones: any[] | null | undefined,
  featuresForFloorPlan: any[] | null | undefined,
  employeeDetails: any,
  userInfo: any,
  booking: FindPersonEmployee,
  setFoundAfterSearch = false
): Promise<void> => {
  // Ensure floorplan panel is visible and state is consistent
  dispatch(setBookingWizardStepState(BookingWizardSteps.STEP_0_INACTIVE))
  dispatch(setDeskBookingFloorplanManuallySelected(true))
  dispatch(setBookingFloorplanViewingDate(new Date()))
    dispatch(
      setDeskBookingSearchParams({
        locationId: booking.locationId,
        floorplanId: booking.floorPlanId,
        employeeID: employeeDetails.employeeId,
        employeeName: `${employeeDetails.employeeForename} ${employeeDetails.employeeSurname}`,
        date: new Date(),
        from: booking.startTime,
        to: booking.endTime,
        autoSearch: false,
        featureId: booking.featureId,
      })
    )
  showComponents(dispatch, {
    floorplan: true,
    navbar: true,
    search: false,
    filter: true,
  })

  // Prepare a SINGLE search window for the selected booking
  const selectedData = {
    selectedFromTo: {
      from: booking.startTime,
      to: booking.endTime,
    },
    searchType: BookingSearchType.SINGLE,
  }

  const singleQuery = true

  // Normalize potentially nullable arrays from state
  const zonesArr = Array.isArray(zones) ? zones : []
  const featuresArr = Array.isArray(featuresForFloorPlan) ? featuresForFloorPlan : []

  // Load features and perform highlighting via the shared search function
  await handleSearch(
    dispatch,
    (err: any) => err,
    singleQuery,
    zonesArr,
    featuresArr,
    employeeDetails,
    userInfo,
    selectedData,
    booking.locationId,
    booking.floorPlanId,
    booking.featureId || null,
    new Date(),
    addDays(new Date(), 1)
  )

  // Optional: explicitly set the found desk to trigger zoom/pan and red flash
  if (setFoundAfterSearch && booking.featureId) {
    dispatch(setFoundDeskFeatureId(booking.featureId))
    if (booking.deskName) {
      dispatch(setFoundDeskName(booking.deskName))
    }
  }
}


export default function FindPerson({ mode = 'search', onShowMeShared }: FindPersonProps) {
  const {
    dailyBookings,
    dailyBookingsLoading,
    zones,
    featuresForFloorPlan
  } = useSelector((state: RootStore) => state.deskBooking)


  const { employeeDetails } = useSelector((state: RootStore) => state.appSettings)
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)
  const dispatch = useDispatch()
  const [findPersonLoading, setFindPersonLoading] = useState(false)
  const [foundPersonData, setFoundPersonData] = useState<FindPersonEmployee[]>([])
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [currentBookingIndex, setCurrentBookingIndex] = useState(0)

  // Transform daily bookings into autocomplete options
  const personOptions: PersonOption[] = dailyBookings.map(booking => ({
    displayName: booking.displayName,
    employeeId: booking.employeeId,
    deskName: booking.deskName,
    floorPlanName: booking.floorPlanName,
  }))


  const uniquePersonOptions = personOptions.filter(
    (person, index, self) => index === self.findIndex(p => p.employeeId === person.employeeId)
  )


  const handleNavigate = async (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' ? currentBookingIndex - 1 : currentBookingIndex + 1
    
    setCurrentBookingIndex(newIndex)
    
    const booking = foundPersonData[newIndex]
    
    if (onShowMeShared && mode === 'search') {
      dispatch(clearFoundDesk())

      await new Promise(resolve => { setTimeout(resolve, 150) })
      
      // Use booking details directly from API response
      const bookingData = {
        locationId: booking.locationId,
        floorplanId: booking.floorPlanId,
        featureId: booking.featureId,
        date: new Date(),
        from: booking.startTime,
        to: booking.endTime,
        autoSearch: true,
        showSearchPane: true
      }
      
      try {
        await onShowMeShared(bookingData)
        if (booking.deskName) {
          dispatch(setFoundDeskName(booking.deskName))
        }
      } catch (error) {
        // Handle error silently
      }
    } else if (mode === 'filter') {
     dispatch(clearFoundDesk())
      
      await displayFloorplanForBooking(
        dispatch,
        Array.isArray(zones) ? zones : [],
        Array.isArray(featuresForFloorPlan) ? featuresForFloorPlan : [],
        employeeDetails,
        userInfo,
        booking,
        true 
      )
    }
  }

  const handlePersonSelection = async (person: PersonOption | null) => {
    try {
      setFindPersonLoading(true)

      setSelectedPerson(person)
      setFoundPersonData([])
      setCurrentBookingIndex(0)

      if (!person) {
        return
      }

      const bookingDetails = await getBookingDetailsForPerson(person)

      setFoundPersonData(bookingDetails)

      if (bookingDetails.length > 0) {
        const firstBooking = bookingDetails[0]
        
        if (mode === 'search') {
          if (onShowMeShared) {
            // Use booking details directly from API response
            const bookingData: BookingSearchParams = {
              locationId: firstBooking.locationId,
              floorplanId: firstBooking.floorPlanId,
              featureId: firstBooking.featureId,
              date: new Date(),
              from: firstBooking.startTime,
              to: firstBooking.endTime,
              autoSearch: true,
              showSearchPane: true
            }

            try {
              await onShowMeShared(bookingData)
              if (firstBooking.deskName) {
                dispatch(setFoundDeskName(firstBooking.deskName))
              }
            } catch (error) {
              // Handle error silently
            }
          } else {
            // Use shared helper to display RHS floorplan and load/highlight features
            await displayFloorplanForBooking(
              dispatch,
              Array.isArray(zones) ? zones : [],
              Array.isArray(featuresForFloorPlan) ? featuresForFloorPlan : [],
              employeeDetails,
              userInfo,
              firstBooking,
              false
            )
            if (firstBooking.deskName) {
              dispatch(setFoundDeskName(firstBooking.deskName))
            }
          }
        } else if (mode === 'filter') {
          // Use the same shared helper; also set found desk after search to flash/zoom
          await displayFloorplanForBooking(
            dispatch,
            Array.isArray(zones) ? zones : [],
            Array.isArray(featuresForFloorPlan) ? featuresForFloorPlan : [],
            employeeDetails,
            userInfo,
            firstBooking,
            true
          )
        }
      }
    } catch (error) {
      setFoundPersonData([])
    } finally {
      setFindPersonLoading(false)
    }
  }

  // Calculate helper text without nested ternaries
  const getHelperText = () => {
    if (selectedPerson) {
      return undefined
    }
    if (inputValue && inputValue.trim().length > 0) {
      return `${uniquePersonOptions.length} people with bookings today - scroll to see all results`
    }
    return `${uniquePersonOptions.length} people found with desk bookings today - start typing to search`
  }

  return (
    <Box marginTop="25px">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            loading={dailyBookingsLoading}
            options={uniquePersonOptions}
            value={selectedPerson}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            onOpen={() => {
              actionsService.userAnalytics
                .postEvent('UIClickEvent', 'FindPersonDropdownClick', window.location.pathname, window.location.hostname)
                .catch((error: unknown) => console.error('Analytics tracking error:', error))
            }}
            noOptionsText="Begin typing to filter results..."
            getOptionLabel={option =>
              `${option.displayName} (${option.floorPlanName})`
            }
            filterOptions={(options, state) => {
              // Only show results if user has typed at least 1 character
              if (!state.inputValue || state.inputValue.trim().length === 0) {
                return []
              }

              // Filter options based on input - no limit, show all matches
              const filtered = options.filter(option => {
                const searchText = state.inputValue.toLowerCase()
                return (
                  option.displayName.toLowerCase().includes(searchText)
                )
              })

              return filtered
            }}
            ListboxProps={{
              style: {
                maxHeight: '150px', // Exactly 5 items visible
                overflow: 'auto',
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Find a person who booked today"
                variant="outlined"
                fullWidth
                helperText={getHelperText()}
              />
            )}
            onChange={(event, value) => handlePersonSelection(value)}
          />
        </Grid>

        {/* Show loading state for findPerson API */}
        {findPersonLoading && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading person details...</Typography>
            </Box>
          </Grid>
        )}

        {/* Show found person data with new component */}
        {foundPersonData.length > 0 && foundPersonData[currentBookingIndex] && (
          <Grid item xs={12}>
            <FindPersonDetails
              personName={selectedPerson?.displayName || ''}
              currentBooking={foundPersonData[currentBookingIndex]}
              currentIndex={currentBookingIndex}
              totalBookings={foundPersonData.length}
              onNavigate={handleNavigate}
              formatTime={formatTimeWithoutSeconds}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  )
}